import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { StickyBox } from 'components/Box'
import { CommonFormContextProps, SaveButton, useCommonFormContextProps } from 'components/Form'
import { PageHeader } from 'components/Header'
import Loading from 'components/Loading'
import Tabs, { getPage, Tab } from 'components/Tabs'
import { ConfirmationDialog, useConfirmationDialog } from 'features/ab-tests'
import { AbTest } from 'features/ab-tests/types/abTest'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { ROUTES } from 'routes'
import { AmazonPageContext } from '../../contexts/AmazonPageContext'
import { AmazonPageContextType } from '../../contexts/AmazonPageContext/types'
import { useGetWorkspaceAmazon } from '../../hooks/useGetWorkspaceAmazon'
import { AmazonTabs, AmazonUrlParams } from './types'
import { useTabs } from './useTabs'

export const AmazonPage = (): React.JSX.Element => {
  const ability = useAbility()
  const { tab } = useParams<AmazonUrlParams>()
  const { t } = useTranslation(['features/globalSetup/amazon', 'common'])

  const { workspaceId } = useWorkspaceParam()
  const { loading, workspaceAmazon } = useGetWorkspaceAmazon({ workspaceId })

  const [activeAbTestId, setActiveAbTestId] = useState<AbTest['id'] | undefined>(undefined)
  const { confirmationHandler, isDialogOpen, setIsDialogOpen } = useConfirmationDialog()

  const tabs: Tab<AmazonTabs>[] = useTabs({ workspaceAmazon })

  const commonContextProps: CommonFormContextProps = useCommonFormContextProps({
    formId: 'amazon-form',
  })
  const contextProps: AmazonPageContextType = {
    setActiveAbTestId,
    workspaceAmazon,
    ...commonContextProps,
  }

  if (loading || tabs.length === 0) {
    return <Loading />
  }

  return (
    <AmazonPageContext.Provider value={contextProps}>
      <StickyBox>
        <AmazonPageContext.Consumer>
          {(ctxProps: AmazonPageContextType) => (
            <>
              <PageHeader
                actions={
                  <SaveButton
                    {...ctxProps}
                    {...(activeAbTestId && { onConfirmation: confirmationHandler })}
                    can={ability.can('update', 'GlobalSetupFeature-amazon')}
                    text={t('common:form.action.save')}
                  />
                }
                title={t('header')}
              />

              {activeAbTestId && (
                <ConfirmationDialog
                  activeAbTestId={activeAbTestId}
                  formProps={commonContextProps}
                  isOpen={isDialogOpen}
                  setOpen={setIsDialogOpen}
                />
              )}
            </>
          )}
        </AmazonPageContext.Consumer>

        <Tabs
          path={ROUTES.GLOBAL_SETUP.AMAZON.ROOT}
          tabs={tabs}
        />
      </StickyBox>

      {getPage<AmazonTabs>(tabs, tab, 'general')}
    </AmazonPageContext.Provider>
  )
}
